import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../api/client';
import type { MinersResponse, MinerStats } from '../api/types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatNumber } from '../util/format';

export const MinersPage = () => {
  const [data, setData] = useState<MinersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const resp = await apiCall<MinersResponse>('/mining');
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return <LoadingSpinner message="Loading miners..." />;
  }

  if (error) {
    return <ErrorMessage error={error} retry={load} />;
  }

  if (!data || !data.miners.length) {
    return (
      <div className="miners-page">
        <h1>Syscoin AuxPoW Miners</h1>
        <p style={{ color: 'var(--color-muted)' }}>No miner data available.</p>
      </div>
    );
  }

  // Calculate summary stats
  const totalSyscoinBlocks = data.miners.reduce((sum, m) => sum + m.blocks, 0);
  const undisclosedMiner = data.miners.find(m => m.name === 'Undisclosed');
  const undisclosedBlocks = undisclosedMiner ? undisclosedMiner.blocks : 0;
  const knownBlocks = totalSyscoinBlocks - undisclosedBlocks;
  const knownMiners = data.miners.length - (undisclosedMiner ? 1 : 0);
  const knownPercentage = totalSyscoinBlocks > 0 ? ((knownBlocks / totalSyscoinBlocks) * 100).toFixed(2) : '0';
  const bitcoinBlocksIndexed = data.bitcoin_blocks_indexed;

  return (
    <div className="miners-page">
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Syscoin AuxPoW Miners</h1>
          <p className="subtitle" style={{ marginTop: '4px' }}>
            Bitcoin mining pools that have merge-mined Syscoin blocks
          </p>
        </div>
      </div>

      {/* Metrics Row */}
      <section className="metrics-row">
        <div className="metric-card">
          <h3>Known Miners</h3>
          <div className="metric-value">{formatNumber(knownMiners)}</div>
          <div className="metric-label">Unique pools identified</div>
        </div>
        <div className="metric-card">
          <h3>Identified</h3>
          <div className="metric-value">{knownPercentage}%</div>
          <div className="metric-label">Blocks with known miner</div>
        </div>
        <div className="metric-card">
          <h3>Bitcoin Blocks</h3>
          <div className="metric-value">{formatNumber(bitcoinBlocksIndexed)}</div>
          <div className="metric-label">Indexed Bitcoin blocks</div>
        </div>
      </section>

      {/* Miners Grid */}
      <h2>Mining Pools ({knownMiners})</h2>
      <section
        className="info-grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
      >
        {data.miners.filter(m => m.name !== 'Undisclosed').map((miner: MinerStats) => {
          const displayName = miner.name || miner.raw_name || 'Unknown';
          const isKnown = miner.name && miner.name !== 'Unknown' && miner.name !== 'Undisclosed';
          const percentage = totalSyscoinBlocks > 0 ? ((miner.blocks / totalSyscoinBlocks) * 100).toFixed(2) : '0';

          return (
            <div
              key={miner.name || miner.raw_name || 'unknown'}
              className="info-item"
              style={{ padding: '1.25rem' }}
            >
              {/* Miner Header */}
              {miner.mempool_id ? (
                <a
                  href={`https://mempool.space/mining/pool/${miner.mempool_id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    marginBottom: '12px',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ fontSize: '1.8em', lineHeight: 1 }}>
                    {isKnown ? '⛏️' : '❓'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        color: isKnown ? '#f7931a' : 'var(--color-muted)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      style={{
                        fontSize: '0.85em',
                        color: 'var(--color-muted)',
                        marginTop: '2px',
                      }}
                    >
                      Bitcoin Mining Pool
                    </div>
                  </div>
                </a>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ fontSize: '1.8em', lineHeight: 1 }}>
                    {isKnown ? '⛏️' : '❓'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        color: isKnown ? '#f7931a' : 'var(--color-muted)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      style={{
                        fontSize: '0.85em',
                        color: 'var(--color-muted)',
                        marginTop: '2px',
                      }}
                    >
                      Bitcoin Mining Pool
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: '6px',
                }}
              >
                <span style={{ color: 'var(--color-muted)', fontSize: '0.9em' }}>
                  Syscoin Blocks:
                </span>
                <span style={{ fontWeight: 600, fontSize: '1.1em' }}>
                  {formatNumber(miner.blocks)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: '8px',
                }}
              >
                <span style={{ color: 'var(--color-muted)', fontSize: '0.9em' }}>
                  Syscoin Commitments:
                </span>
                {miner.mempool_id ? (
                  <Link
                    to={`/mining/pool/${miner.mempool_id}`}
                    style={{
                      fontWeight: 600,
                      fontSize: '1.1em',
                      color: '#f7931a',
                      textDecoration: 'none',
                    }}
                  >
                    {formatNumber(miner.syscoin_commitments)}
                  </Link>
                ) : (
                  <span style={{ fontWeight: 600, fontSize: '1.1em', color: '#f7931a' }}>
                    {formatNumber(miner.syscoin_commitments)}
                  </span>
                )}
              </div>

              {/* Share percentage bar */}
              <div style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.8em',
                    color: 'var(--color-muted)',
                    marginBottom: '4px',
                  }}
                >
                  <span>Share</span>
                  <span>{percentage}%</span>
                </div>
                <div
                  style={{
                    height: '4px',
                    background: 'var(--color-border)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${percentage}%`,
                      background: isKnown ? '#f7931a' : 'var(--color-muted)',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Info Section */}
      <h2>About AuxPoW Mining</h2>
      <div className="navigation-card">
        <p style={{ margin: '0 0 12px 0', lineHeight: 1.6 }}>
          Syscoin uses <strong>Auxiliary Proof-of-Work (AuxPoW)</strong> to inherit Bitcoin's
          hashrate security through merge-mining. Bitcoin miners can simultaneously mine Syscoin
          blocks without additional computational cost.
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
          <li>Miners include a commitment to Syscoin's block hash in their Bitcoin coinbase transaction</li>
          <li>When a Bitcoin block is found, it also validates the Syscoin block</li>
          <li>This provides Bitcoin-level security for Syscoin's UTXO layer</li>
        </ul>
      </div>
    </div>
  );
};
